import { GCashButton } from '@src/components/Button';
import { OptimizedImage } from '@src/components/OptimizedImage';
import { PortfolioHUD } from '@src/components/PortfolioHUD';
import { useBalanceByCurrency } from '@src/hooks/useBalanceByCurrency';
import { toBigWithDefault, useCurrenciesContext, usePortal, useSessionStorage } from '@talos/kyoko';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { AnimatedStackOutlet } from '../../components/AnimatedStackOutlet';
import { useStackableRouteId } from '../../hooks/useStackableRouteId';
import { PortfolioItem } from './PortfolioItem';

export function PortfolioLayout() {
  const portalId = useStackableRouteId();
  const { setPortalRef } = usePortal(portalId);
  const match = useMatch('/portfolio');

  return (
    <>
      <AnimatedStackOutlet />
      <div className={clsx('flex w-full grow', match === null && 'pointer-events-none')}>
        {/* Base background route: always rendered */}
        <PortfolioPage />
        {/* Stack routes (like order form) */}
      </div>
      <div id={`${portalId}-portal`} ref={setPortalRef} />
    </>
  );
}

export const PortfolioPage = () => {
  const navigate = useNavigate();
  const { currenciesList: _allCurrencies } = useCurrenciesContext();
  const balanceByCurrency = useBalanceByCurrency();
  const currenciesList = useMemo(() => {
    return Array.from(_allCurrencies.values()).filter(
      c => !toBigWithDefault(balanceByCurrency?.get(c.Symbol)?.Amount, 0).eq(0)
    );
  }, [balanceByCurrency, _allCurrencies]);

  const [sort, setSort] = useSessionStorage<'asc' | 'desc'>('portfolio-sort', 'asc');
  const [sortClickCount, setSortClickCount] = useState(0);

  const [search, setSearch] = useSessionStorage('portfolio-search', '');

  const filteredCurrencies = useMemo(() => {
    return currenciesList.filter(
      currency =>
        currency.Symbol.toLowerCase().includes(search.toLowerCase()) ||
        currency.Description?.toLowerCase().includes(search.toLowerCase())
    );
  }, [currenciesList, search]);

  const sortedCurrencies = useMemo(() => {
    return filteredCurrencies.toSorted((a, b) => {
      const aBalance = toBigWithDefault(balanceByCurrency?.get(a.Symbol)?.Equivalent?.Amount, 0);
      const bBalance = toBigWithDefault(balanceByCurrency?.get(b.Symbol)?.Equivalent?.Amount, 0);
      return sort === 'asc' ? aBalance.cmp(bBalance) : bBalance.cmp(aBalance);
    });
  }, [balanceByCurrency, filteredCurrencies, sort]);

  const showBlankScreen = currenciesList.length === 0;

  return (
    <div className="flex size-full flex-col gap-2" data-testid="portfolio-page">
      <div className="p-3">
        <PortfolioHUD />
      </div>

      {balanceByCurrency === undefined ? null : (
        <>
          {showBlankScreen && (
            <div className="flex w-4/5 flex-col gap-4 place-self-center">
              <div className="flex aspect-[1.47] w-full justify-center">
                <OptimizedImage name="savings" widths={[360, 720]} alt="Savings" />
              </div>
              <div className="text-center">
                <h2 className="text-color-text mb-1 text-2xl font-semibold">Start your crypto journey!</h2>
                <h5 className="text-color-text-weak">Top up so you can start trading.</h5>
                <h5 className="text-color-text-weak">Go to the dashboard to view available coins.</h5>
              </div>
              <GCashButton
                size="lg"
                variant="primary"
                onClick={() => {
                  navigate('/watchlist');
                }}
              >
                Lets go!
              </GCashButton>
            </div>
          )}

          {!showBlankScreen && (
            <div className="w-full">
              <div className="border-color-border bg-color-bg-main flex flex-col">
                <div className="border-color-border flex items-center gap-2 border-b px-4 py-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path
                      d="M10 3.5C13.866 3.5 17 6.63401 17 10.5C17 12.0723 16.4807 13.5228 15.6055 14.6914L20.207 19.293L20.2754 19.3691C20.5957 19.7619 20.5731 20.3409 20.207 20.707C19.8409 21.0731 19.2619 21.0957 18.8691 20.7754L18.793 20.707L14.1914 16.1055C13.0228 16.9807 11.5723 17.5 10 17.5C6.13401 17.5 3 14.366 3 10.5C3 6.63401 6.13401 3.5 10 3.5ZM10 5.5C7.23858 5.5 5 7.73858 5 10.5C5 13.2614 7.23858 15.5 10 15.5C12.7614 15.5 15 13.2614 15 10.5C15 7.73858 12.7614 5.5 10 5.5Z"
                      fill="#445C85"
                    />
                  </svg>
                  <input
                    className="text-color-text-weak flex-1 px-2 py-1 text-base font-semibold"
                    placeholder="Search"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
                <div className="flex w-full items-center justify-between p-3">
                  <h3 className="text-color-text text-lg font-semibold">My Crypto</h3>
                  <div className="relative">
                    <button
                      className="bg-color-bg-strong flex items-center justify-start gap-1 rounded-full px-3 py-1 text-base font-semibold text-gray-400"
                      data-testid="sort-button"
                      onClick={() => {
                        setSort(sort === 'asc' ? 'desc' : 'asc');
                        setSortClickCount(p => p + 1);
                      }}
                    >
                      <span className="text-color-text-weaker">Sort</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        className="transition-transform duration-200"
                        style={{
                          transform: `rotate(${sortClickCount * 180}deg)`,
                        }}
                      >
                        <path
                          d="M6.75 10.25L12 15.5L17.25 10.25"
                          stroke="#005CE5"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex flex-col">
                  {sortedCurrencies.map(currency => {
                    const amt = balanceByCurrency.get(currency.Symbol)?.Amount;
                    if (!amt || amt === '0') {
                      return null;
                    }
                    return <PortfolioItem key={currency.Symbol} currency={currency} />;
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
